/**
 * TypeScript mirror of the KPI workflow v1 API contract, plus the boundary
 * parsers every response passes through.
 *
 * Source of truth: `backend/app/modules/kpi/api/workflow_router.py`
 * (`/api/kpi/workflows`, `/api/kpi/metric-definitions`) and the schemas beside
 * it. The dashboard's read surface (`/api/kpi/values`, `/api/kpi/table`) is a
 * different router with its own types. Timestamps arrive as epoch-ms ints (backend JSON rule);
 * reporting periods arrive as ISO `YYYY-MM-DD` because a period is a calendar
 * fact, not an instant.
 *
 * Validation is hand-rolled rather than Zod, following `types/cabinet.ts` and
 * `types/evidenceLineage.ts`: Zod v4's `.d.ts` does not compile under this
 * repo's TypeScript (`z.enum` alone breaks the build), so a schema here would
 * cost the static types it exists to provide.
 *
 * A malformed payload THROWS rather than degrading to a default. These screens
 * drive extraction runs, and a workflow silently rendered with a wrong scope or
 * a dropped metric is worse than an error message.
 */

export const FOLDER_SCOPES = ["all", "selected"] as const;
export const PERIOD_INTERVALS = ["monthly", "quarterly", "weekly"] as const;
export const RUN_TRIGGERS = ["manual", "file_added", "backfill"] as const;
export const RUN_STATUSES = [
	"queued",
	"running",
	"completed",
	"failed",
	"cancelled",
] as const;

export type FolderScope = (typeof FOLDER_SCOPES)[number];
export type PeriodInterval = (typeof PERIOD_INTERVALS)[number];
export type RunTrigger = (typeof RUN_TRIGGERS)[number];
export type RunStatus = (typeof RUN_STATUSES)[number];

export const isPeriodInterval = (value: string): value is PeriodInterval =>
	(PERIOD_INTERVALS as readonly string[]).includes(value);

/**
 * The only interval the extraction engine executes today.
 *
 * `PERIOD_INTERVALS` stays at three because the schema is weekly-ready by
 * design — the fact table stores a date plus a grain so weekly needs no
 * migration. The engine is not: it derives one period per file and treats every
 * workflow as monthly. The composer therefore keeps the control and disables the
 * rest rather than hiding them, and the API rejects them (422) rather than
 * accepting a grain it would silently ignore — a wrong `period_grain` on a
 * stored fact outlives the mistake that made it.
 *
 * Epic decision, integrated codex round 1 (F7). Mirrors
 * `schemas/workflow.py::SUPPORTED_INTERVAL`.
 */
export const SUPPORTED_INTERVAL: PeriodInterval = "monthly";

export const isSupportedInterval = (value: PeriodInterval): boolean =>
	value === SUPPORTED_INTERVAL;

/**
 * A run is done when it can no longer change on its own.
 *
 * `cancelled` belongs here and is NOT a failure: the run stopped because
 * someone asked it to, and everything it wrote before that is correct.
 */
export const TERMINAL_RUN_STATUSES: readonly RunStatus[] = [
	"completed",
	"failed",
	"cancelled",
];

export const isTerminalRunStatus = (status: RunStatus): boolean =>
	TERMINAL_RUN_STATUSES.includes(status);

export interface SkippedFile {
	file: string;
	reason: string;
}

export interface WorkflowRun {
	id: string;
	workflow_id: string;
	trigger: RunTrigger;
	status: RunStatus;
	model: string;
	thinking_level: string;
	files_total: number;
	files_processed: number;
	/** Always present, possibly empty — a skip is reported, never silent. */
	skipped: SkippedFile[];
	/**
	 * NULL until the engine claims the run. `kpi_workflow_runs.started_at` is
	 * nullable, and a queued run genuinely has no start time — the UI shows that
	 * as "not started yet" rather than pretending to a timestamp.
	 */
	started_at_ms: number | null;
	finished_at_ms: number | null;
	error: string | null;
	/**
	 * What the run did to the values table: how many it wrote, and how many of
	 * those displaced a number the table was already showing.
	 *
	 * NULL for runs whose values predate run stamping — the backend has no
	 * record of what they changed, and rendering that as 0 would claim they
	 * changed nothing. Optional on the wire, so old responses still parse.
	 */
	values_written: number | null;
	values_revised: number | null;
	/**
	 * Whether this run ignored the "already extracted" check and re-read content
	 * the client already had values from. A property of the run, not of the
	 * request — it is what explains an unusually expensive one afterwards.
	 * Absent on responses that predate the flag, which read as false: those runs
	 * were ordinary.
	 */
	forced: boolean;
	/**
	 * A stop has been asked for and the run has not reached a terminal state yet
	 * — it is finishing the file in hand. The affordance reads from this so a
	 * cancel already given is not offered again.
	 */
	cancel_requested: boolean;
	/**
	 * This run's values were later taken back off the table and the numbers it
	 * had replaced restored. The status is unchanged — the run did finish.
	 * Absent on responses that predate the flag, which read as false.
	 */
	undone: boolean;
	/**
	 * Where this run stands as a VERSION of the table. Each finished run is the
	 * table as it stood when that run finished: `is_current` marks the one whose
	 * results are showing, and `can_restore` says the table can be set to this
	 * one instead — back to an older run, or forward again to one that was
	 * undone. The server decides both, and only the run history carries them;
	 * everywhere else they read as false.
	 */
	is_current: boolean;
	can_restore: boolean;
}

export interface Workflow {
	id: string;
	name: string;
	template_key: string;
	folder_scope: FolderScope;
	folder_ids: number[];
	metric_keys: string[];
	period_start: string | null;
	period_end: string | null;
	interval: PeriodInterval;
	auto_run: boolean;
	model: string;
	thinking_level: string;
	created_at_ms: number;
	last_run: WorkflowRun | null;
}

export interface MetricDefinition {
	key: string;
	display_name: string;
	metric_group: string;
	unit: string;
	aggregation: string;
	is_raw: boolean;
}

export interface MetricGroup {
	metric_group: string;
	/** The section heading (DOP-613); older servers send none. */
	label?: string;
	metrics: MetricDefinition[];
}

/** Body of `POST /api/kpi/workflows`. */
export interface CreateWorkflowPayload {
	name: string;
	template_key?: string;
	folder_scope: FolderScope;
	folder_ids: number[];
	metric_keys: string[];
	period_start: string | null;
	period_end: string | null;
	interval: PeriodInterval;
	auto_run: boolean;
}

export class MalformedResponseError extends Error {
	constructor(what: string) {
		super(`The server sent an unexpected ${what}.`);
		this.name = "MalformedResponseError";
	}
}

// ---------------------------------------------------------------------------
// Boundary parsing
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireString(value: unknown, what: string): string {
	if (typeof value !== "string") throw new MalformedResponseError(what);
	return value;
}

function requireNumber(value: unknown, what: string): number {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		throw new MalformedResponseError(what);
	}
	return value;
}

function nullableString(value: unknown): string | null {
	return typeof value === "string" ? value : null;
}

function nullableNumber(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function requireOneOf<T extends string>(
	value: unknown,
	allowed: readonly T[],
	what: string,
): T {
	if (typeof value !== "string" || !(allowed as readonly string[]).includes(value)) {
		throw new MalformedResponseError(what);
	}
	return value as T;
}

function requireArray(value: unknown, what: string): unknown[] {
	if (!Array.isArray(value)) throw new MalformedResponseError(what);
	return value;
}

function parseSkipped(value: unknown): SkippedFile[] {
	return requireArray(value, "skip list").map((entry) => {
		if (!isRecord(entry)) throw new MalformedResponseError("skip entry");
		return {
			file: requireString(entry.file, "skipped file name"),
			reason: requireString(entry.reason, "skip reason"),
		};
	});
}

export function parseRun(raw: unknown): WorkflowRun {
	if (!isRecord(raw)) throw new MalformedResponseError("run");
	return {
		id: requireString(raw.id, "run id"),
		workflow_id: requireString(raw.workflow_id, "workflow id"),
		trigger: requireOneOf(raw.trigger, RUN_TRIGGERS, "run trigger"),
		status: requireOneOf(raw.status, RUN_STATUSES, "run status"),
		model: requireString(raw.model, "run model"),
		thinking_level: requireString(raw.thinking_level, "thinking level"),
		files_total: requireNumber(raw.files_total, "file count"),
		files_processed: requireNumber(raw.files_processed, "file count"),
		skipped: parseSkipped(raw.skipped),
		started_at_ms: nullableNumber(raw.started_at_ms),
		finished_at_ms: nullableNumber(raw.finished_at_ms),
		error: nullableString(raw.error),
		values_written: nullableNumber(raw.values_written),
		values_revised: nullableNumber(raw.values_revised),
		forced: raw.forced === true,
		cancel_requested: raw.cancel_requested === true,
		undone: raw.undone === true,
		is_current: raw.is_current === true,
		can_restore: raw.can_restore === true,
	};
}

export function parseWorkflow(raw: unknown): Workflow {
	if (!isRecord(raw)) throw new MalformedResponseError("workflow");
	return {
		id: requireString(raw.id, "workflow id"),
		name: requireString(raw.name, "workflow name"),
		template_key: requireString(raw.template_key, "template key"),
		folder_scope: requireOneOf(raw.folder_scope, FOLDER_SCOPES, "folder scope"),
		folder_ids: requireArray(raw.folder_ids, "folder list").map((id) =>
			requireNumber(id, "folder id"),
		),
		metric_keys: requireArray(raw.metric_keys, "metric list").map((key) =>
			requireString(key, "metric key"),
		),
		period_start: nullableString(raw.period_start),
		period_end: nullableString(raw.period_end),
		interval: requireOneOf(raw.interval, PERIOD_INTERVALS, "interval"),
		auto_run: Boolean(raw.auto_run),
		model: requireString(raw.model, "model"),
		thinking_level: requireString(raw.thinking_level, "thinking level"),
		created_at_ms: requireNumber(raw.created_at_ms, "created timestamp"),
		last_run:
			raw.last_run === null || raw.last_run === undefined
				? null
				: parseRun(raw.last_run),
	};
}

export function parseWorkflowList(raw: unknown): Workflow[] {
	if (!isRecord(raw)) throw new MalformedResponseError("workflow list");
	return requireArray(raw.workflows, "workflow list").map(parseWorkflow);
}

export function parseRunList(raw: unknown): WorkflowRun[] {
	if (!isRecord(raw)) throw new MalformedResponseError("run list");
	return requireArray(raw.runs, "run list").map(parseRun);
}

function parseMetricDefinition(raw: unknown): MetricDefinition {
	if (!isRecord(raw)) throw new MalformedResponseError("metric");
	return {
		key: requireString(raw.key, "metric key"),
		display_name: requireString(raw.display_name, "metric name"),
		metric_group: requireString(raw.metric_group, "metric group"),
		unit: requireString(raw.unit, "metric unit"),
		aggregation: requireString(raw.aggregation, "aggregation rule"),
		is_raw: Boolean(raw.is_raw),
	};
}

export function parseMetricCatalog(raw: unknown): MetricGroup[] {
	if (!isRecord(raw)) throw new MalformedResponseError("metric catalog");
	return requireArray(raw.groups, "metric catalog").map((group) => {
		if (!isRecord(group)) throw new MalformedResponseError("metric group");
		return {
			metric_group: requireString(group.metric_group, "metric group name"),
			label: typeof group.label === "string" ? group.label : undefined,
			metrics: requireArray(group.metrics, "metric list").map(
				parseMetricDefinition,
			),
		};
	});
}

// ---------------------------------------------------------------------------
// Display vocabulary
// ---------------------------------------------------------------------------

export const INTERVAL_LABELS: Record<PeriodInterval, string> = {
	monthly: "Monthly",
	quarterly: "Quarterly",
	weekly: "Weekly",
};

export const RUN_STATUS_LABELS: Record<RunStatus, string> = {
	queued: "Queued",
	running: "Running",
	completed: "Completed",
	failed: "Failed",
	cancelled: "Cancelled",
};

export const RUN_TRIGGER_LABELS: Record<RunTrigger, string> = {
	manual: "Run now",
	file_added: "New file",
	backfill: "Backfill",
};
